/**
 * <benefit-scene> — sections/product-benefits.liquid.
 *
 * Reveals the composition piece by piece as it scrolls in. Every [data-reveal] element gets its
 * own observer entry rather than the section getting one for all of them: the stage is nearly a
 * full screen tall, so a single trigger on the section fires while the lower cards are still
 * below the fold and the visitor never sees them arrive.
 *
 * A card also reveals the dashed line that belongs to it, matched by data-reveal-group within
 * the same layout (the stage and the fallback row both number their cards 1..3). The rest is
 * CSS: the line draws towards the bottle, the dot lands on the glass, and the bottle starts
 * levitating once it is in.
 *
 * With `prefers-reduced-motion: reduce`, everything is marked revealed at once and the CSS skips
 * the transitions and the levitation.
 */

/** Elements must clear this much of the viewport bottom before they count as seen. */
const ROOT_MARGIN = '0px 0px -18% 0px';

class BenefitScene extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    const targets = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-reveal]')]);
    if (targets.length === 0) return;

    if (
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      !('IntersectionObserver' in window)
    ) {
      for (const target of targets) this.#reveal(target);
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#reveal(/** @type {HTMLElement} */ (entry.target));
          this.#observer?.unobserve(entry.target);
        }
      },
      { rootMargin: ROOT_MARGIN, threshold: 0 }
    );

    for (const target of targets) this.#observer.observe(target);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /** @param {HTMLElement} target */
  #reveal(target) {
    target.setAttribute('data-revealed', '');

    const group = target.dataset.revealGroup;
    if (!group) return;

    // Stay inside this layout: the stage and the row both carry cards numbered 1..3.
    const layout = target.closest('.pb-stage, .pb-row') ?? this;
    for (const peer of layout.querySelectorAll(`[data-reveal-group="${group}"]`)) {
      peer.setAttribute('data-revealed', '');
    }
  }
}

if (!customElements.get('benefit-scene')) {
  customElements.define('benefit-scene', BenefitScene);
}
